// 引入 className 合并工具。
import cx from "classnames";
// 引入时间格式化库。
import dayjs from "dayjs";
// 引入通用 Head 组件。
import CommonHead from "@/components/CommonHead";
// 引入通用顶部导航组件。
import CommonHeader from "@/components/CommonHeader";
// 引入通用图片组件。
import CommonImage, { ImageType } from "@/components/CommonImage";
// 引入通用图标组件。
import CommonIcon from "@/components/CommonIcon";
// 引入自定义 Tooltip 组件。
import CustomTooltip from "@/components/CustomTooltip";
// 引入侧栏组件类型。
import { SidebarList, HomeSidebarListOption } from "@/components/HomeSidebar/SideItems";
// 引入侧栏底部组件。
import SideBarFooter from "@/components/HomeSidebar/SideBarFooter";
// 引入设备弹窗。
import MyDevicesModal from "../components/MyDevicesModal";
// 引入积分兑换弹窗。
import PointsExchangeModal from "../components/PointsExchangeModal";
// 引入编辑资料弹窗。
import EditProfileModal from "../components/EditProfileModal";
import VipIcon from "../../../assets/icon/account/vip.svg";
import PreviewCloseIcon from "../../../assets/icon/account/pc-preview-close.svg";
import PreviewOpenIcon from "../../../assets/icon/account/pc-preview-open.svg";
import GoToInfoIcon from "../../../assets/icon/account/go-to-info-pc.svg";
import AddPostIcon from "../../../assets/icon/homeSidebar/add-post.svg";
import { IUserEquipment } from "@/api/device";
import { IUserLoginInfo, IUserInfo } from "@/api/user";
import { ITopLevelNav } from "@/api/nav";
import styles from "../index.module.scss";

// PC 布局组件入参类型。
interface Props {
  t: (key: string, values?: any) => string;
  seoTitle: string;
  seoDescription: string;
  seoKeywords: string;
  account: IUserLoginInfo;
  navList: ITopLevelNav[];
  saltkey: string;
  isSelfView: boolean;
  isAdminView: boolean;
  displayAccount: any;
  displayName: string;
  shouldShowAdminNameTooltip: boolean;
  badgeIconError: boolean;
  setBadgeIconError: (val: boolean) => void;
  handleEditProfileClick: () => void;
  showFlightInfo: boolean;
  displayFlightDistance: string;
  hideDistance: boolean;
  handleToggleFlightVisibility: () => void;
  handlePointsExchangeClick: () => void;
  displayPoints: string | number;
  deviceCount: number;
  previewDevices: IUserEquipment[];
  showMoreDevices: boolean;
  handleDeviceIconClick: (deviceLink?: string, productName?: string) => void;
  setShowDevicesModal: (visible: boolean) => void;
  adminUid: any;
  adminEmail: any;
  adminIp: any;
  adminLocation: any;
  adminLastLoginTime: any;
  sidebarOptions: HomeSidebarListOption[];
  activeTab: string;
  handlePost: () => void;
  visibleTabKeys: string[];
  renderTabPanel: (tabKey: string) => React.ReactNode;
  transparentMain: boolean;
  showDevicesModal: boolean;
  initialDevices: IUserEquipment[];
  showPointsExchangeModal: boolean;
  setShowPointsExchangeModal: (v: boolean) => void;
  setPoints: (v: number) => void;
  showEditProfileModal: boolean;
  setShowEditProfileModal: (v: boolean) => void;
  initialUserInfo: IUserInfo | null | undefined;
  profilePatch: { name?: string; avatarUrl?: string } | null;
  refreshGlobalAccount: () => void;
  setProfilePatch: (payload: any) => void;
  onCreditsChange: (newCredits: number) => void;
}

// 用户中心 PC 页面布局组件。
export default function UserCenterPcLayout(props: Props) {
  const {
    t,
    seoTitle,
    seoDescription,
    seoKeywords,
    account,
    navList,
    saltkey,
    isSelfView,
    isAdminView,
    displayAccount,
    displayName,
    shouldShowAdminNameTooltip,
    badgeIconError,
    setBadgeIconError,
    handleEditProfileClick,
    showFlightInfo,
    displayFlightDistance,
    hideDistance,
    handleToggleFlightVisibility,
    handlePointsExchangeClick,
    displayPoints,
    deviceCount,
    previewDevices,
    showMoreDevices,
    handleDeviceIconClick,
    setShowDevicesModal,
    adminUid,
    adminEmail,
    adminIp,
    adminLocation,
    adminLastLoginTime,
    sidebarOptions,
    activeTab,
    handlePost,
    visibleTabKeys,
    renderTabPanel,
    transparentMain,
    showDevicesModal,
    initialDevices,
    showPointsExchangeModal,
    setShowPointsExchangeModal,
    setPoints,
    showEditProfileModal,
    setShowEditProfileModal,
    initialUserInfo,
    profilePatch,
    refreshGlobalAccount,
    setProfilePatch,
    onCreditsChange,
  } = props;

  const formatAdminValue = (val: any) => (val ? val : "--");
  // 管理员最后登录时间格式化。
  const formatAdminTime = (val: any) => {
    if (!val) return "--";
    return dayjs.unix(Number(val)).format("YYYY-MM-DD");
  };

  // 渲染页面结构：head/header/banner/sidebar/content/modals。
  return (
    <div className={styles.root}>
      <CommonHead title={seoTitle} description={seoDescription} keywords={seoKeywords} />
      <div className={styles.header}>
        <CommonHeader
          hasNav={true}
          account={account}
          navsList={navList || []}
          saltkey={saltkey}
          onCreditsChange={onCreditsChange}
        />
      </div>
      <div className={styles.mainContainer}>
        {/* 用户信息 Banner 区 */}
        <section className={styles.userInfoBannerContainer}>
          <div className={cx(styles.userInfoBanner, { [styles.userInfoBannerAdmin]: isAdminView && showFlightInfo })}>
            <div className={styles.userProfile}>
              <CommonImage
                className={styles.avatar}
                src={displayAccount?.avatar?.large || ""}
                type={ImageType.avatar}
                layout="fill"
                cutMode="cover"
                alt={displayAccount?.name}
              />
              <div className={styles.info}>
                <div className={styles.nameRow}>
                  {shouldShowAdminNameTooltip ? (
                    <CustomTooltip title={displayName} placement="top">
                      <h1 className={cx(styles.name, { [styles.nameAdmin]: isAdminView })}>{displayName}</h1>
                    </CustomTooltip>
                  ) : (
                    <h1 className={cx(styles.name, { [styles.nameAdmin]: isAdminView })}>{displayName}</h1>
                  )}
                  {isSelfView && (
                    <div className={styles.editButton} onClick={handleEditProfileClick}>
                      <CommonIcon icon="account-edit" className={styles.editIcon} />
                    </div>
                  )}
                </div>
                <div className={styles.tagsRow}>
                  {displayAccount?.rank?.vname && (
                    <div className={cx(styles.badge, styles.verifyBadge)}>
                      <VipIcon className={styles.verifyIcon} />
                      <span className={styles.badgeText}>{displayAccount.rank.vname}</span>
                    </div>
                  )}
                  {displayAccount?.group && (
                    <div className={styles.badge}>
                      {!badgeIconError && displayAccount.group.icon && (
                        <CommonImage
                          className={styles.badgeIcon}
                          src={displayAccount.group.icon}
                          type={ImageType.icon}
                          layout={[20, 20]}
                          cutMode="contain"
                          alt={displayAccount.group.title}
                          onError={() => setBadgeIconError(true)}
                        />
                      )}
                      <span className={styles.badgeText}>{displayAccount.group.title}</span>
                    </div>
                  )}
                  {!isAdminView && showFlightInfo && (
                    <div className={styles.flightInfo}>
                      <CommonIcon icon="account-send" className={styles.planeIcon} />
                      <span className={styles.flightText}>{t("user_profile_flight_distance")}</span>
                      <span className={styles.flightDistance}>{displayFlightDistance}</span>
                      {isSelfView && (
                        <CustomTooltip
                          title={hideDistance ? t("user_center_pc_flight_private_tip") : t("user_center_pc_flight_public_tip")}
                          placement="top"
                          className={styles.eyeIconWrapper}
                        >
                          <div className={styles.eyeIcon} role="button" tabIndex={0} aria-label={t("user_center_pc_toggle_visibility")} onClick={handleToggleFlightVisibility}>
                            {hideDistance ? <PreviewCloseIcon /> : <PreviewOpenIcon />}
                          </div>
                        </CustomTooltip>
                      )}
                    </div>
                  )}
                </div>
                {isAdminView && showFlightInfo && (
                  <div className={styles.flightInfo} style={{ marginTop: "2px" }}>
                    <CommonIcon icon="account-send" className={styles.planeIcon} />
                    <span className={styles.flightText}>{t("user_profile_flight_distance")}</span>
                    <span className={styles.flightDistance}>{displayFlightDistance}</span>
                    {isSelfView && (
                      <CustomTooltip
                        title={hideDistance ? t("user_center_pc_flight_private_tip") : t("user_center_pc_flight_public_tip")}
                        placement="top"
                        className={styles.eyeIconWrapper}
                      >
                        <div className={styles.eyeIcon} role="button" tabIndex={0} aria-label={t("user_center_pc_toggle_visibility")} onClick={handleToggleFlightVisibility}>
                          {hideDistance ? <PreviewCloseIcon /> : <PreviewOpenIcon />}
                        </div>
                      </CustomTooltip>
                    )}
                  </div>
                )}
              </div>
            </div>
            <div className={styles.userStats}>
              <div className={styles.statCard}>
                <div className={styles.cardHeader}>
                  <span>{isSelfView ? t("user_center_pc_my_points") : t("user_center_pc_ta_points")}</span>
                  {isSelfView && (
                    <span className={styles.arrow} role="button" tabIndex={0} onClick={handlePointsExchangeClick} style={{ cursor: "pointer" }}>
                      {t("user_profile_action_exchange")} <GoToInfoIcon className={styles.icon} />
                    </span>
                  )}
                </div>
                <div className={styles.cardValue}>{displayPoints}</div>
              </div>
              <div className={cx(styles.statCard, { [styles.clickable]: deviceCount > 4 })} onClick={() => deviceCount > 4 && setShowDevicesModal(true)}>
                <div className={styles.cardHeader}>
                  <span>{isSelfView ? t("user_center_pc_my_devices") : t("user_center_pc_ta_devices")}</span>
                  <span className={styles.arrow}>
                    {typeof deviceCount === "number" ? t("user_profile_devices_count", { count: deviceCount }) : t("user_profile_devices_count", { count: 0 })}
                    {deviceCount > 4 && <GoToInfoIcon className={styles.icon} />}
                  </span>
                </div>
                <div className={styles.deviceList}>
                  {typeof deviceCount === "number" && deviceCount <= 0 ? (
                    <div className={cx(styles.deviceIcon, styles.noDevice)}>
                      <CommonIcon icon="empty-device" />
                    </div>
                  ) : (
                    <>
                      {previewDevices.length > 0
                        ? previewDevices.map((d: IUserEquipment, idx: number) => {
                            const deviceLink = d?.home_page;
                            return (
                              <div
                                key={deviceLink || d?.name || d?.icon || idx}
                                className={styles.deviceIcon}
                                role={deviceLink ? "button" : undefined}
                                tabIndex={deviceLink ? 0 : undefined}
                                aria-label={deviceLink ? t("user_center_pc_view_device", { name: d?.name || "" }) : undefined}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDeviceIconClick(deviceLink, d?.name);
                                }}
                                onKeyDown={(e) => {
                                  if (!deviceLink) return;
                                  if (e.key === "Enter" || e.key === " ") {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    handleDeviceIconClick(deviceLink, d?.name);
                                  }
                                }}
                              >
                                <CommonImage src={d?.icon || ""} type={ImageType.device} layout={[28, 28]} cutMode="contain" alt={d?.name} />
                              </div>
                            );
                          })
                        : Array.from({ length: Math.min(4, deviceCount || 0) }, (_, idx) => (
                            <div key={idx} className={styles.deviceIcon}>
                              <CommonImage src="" type={ImageType.device} layout={[28, 28]} cutMode="contain" alt="device" />
                            </div>
                          ))}
                      {showMoreDevices && (
                        <div className={styles.deviceIcon}>
                          <CommonIcon icon="my-device-more" />
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
              {isAdminView && (
                <div
                  className={styles.adminInfo}
                  style={(adminEmail && adminEmail.length > 24) || (adminLocation && adminLocation.length > 3) ? { marginTop: "-10px" } : {}}
                >
                  <div className={styles.adminInfoRow}>
                    <span className={styles.label}>UID：</span>
                    <span className={styles.value}>{formatAdminValue(adminUid)}</span>
                  </div>
                  <div className={styles.adminInfoRow}>
                    <span className={styles.label}>{t("user_profile_admin_email_label")}</span>
                    <span className={styles.value}>{formatAdminValue(adminEmail)}</span>
                  </div>
                  <div className={styles.adminInfoRow}>
                    <span className={styles.label}>IP：</span>
                    <span className={styles.value}>{formatAdminValue(adminIp)}</span>
                    <span className={styles.divider}>|</span>
                    <span className={styles.label}>{t("user_profile_admin_location_label")}</span>
                    <span className={styles.value}>{formatAdminValue(adminLocation)}</span>
                  </div>
                  <div className={styles.adminInfoRow}>
                    <span className={styles.label}>{t("user_profile_admin_last_login_label")}</span>
                    <span className={styles.value}>{formatAdminTime(adminLastLoginTime)}</span>
                  </div>
                </div>
              )}
            </div>
          </div>
        </section>
        <div className={styles.contentBody}>
          {/* 本人视角左侧导航区 */}
          {isSelfView && (
            <aside className={styles.sidebar}>
              <nav>
                <SidebarList options={sidebarOptions} activeKey={activeTab} disableResponsive />
              </nav>
              <div className={styles.actionArea}>
                <button className={styles.postButton} onClick={handlePost}>
                  <span className={styles.icon}>
                    <AddPostIcon />
                  </span>
                  {t("user_center_pc_post")}
                </button>
              </div>
              <div className={styles.sidebarFooter}>
                <SideBarFooter disableResponsive />
              </div>
            </aside>
          )}
          {/* 右侧主内容区（各 tab 面板） */}
          <main className={cx(styles.mainContent, { [styles.transparentBg]: transparentMain })}>
            {visibleTabKeys.map((tabKey) => (
              <div key={tabKey} className={cx(styles.tabPanel, { [styles.tabPanelActive]: activeTab === tabKey, [styles.tabPanelHidden]: activeTab !== tabKey })}>
                {renderTabPanel(tabKey)}
              </div>
            ))}
          </main>
        </div>
      </div>
      {/* 设备弹窗 */}
      <MyDevicesModal visible={showDevicesModal} onClose={() => setShowDevicesModal(false)} devices={initialDevices} isSelfView={isSelfView} onDeviceClick={handleDeviceIconClick} />
      {/* 积分兑换弹窗 */}
      <PointsExchangeModal visible={showPointsExchangeModal} onClose={() => setShowPointsExchangeModal(false)} onPointsUpdate={setPoints} />
      {/* 编辑资料弹窗 */}
      <EditProfileModal
        visible={showEditProfileModal}
        onClose={() => setShowEditProfileModal(false)}
        userInfo={
          initialUserInfo
            ? {
                ...initialUserInfo,
                name: profilePatch?.name || initialUserInfo.name,
                avatar: profilePatch?.avatarUrl || initialUserInfo.avatar,
              }
            : null
        }
        account={account}
        onSuccess={(payload) => {
          if (payload?.name || payload?.avatarUrl) {
            setProfilePatch((prev: any) => ({
              ...prev,
              ...payload,
            }));
          }
          refreshGlobalAccount();
        }}
      />
    </div>
  );
}
